"use client";

import Link from "next/link";
import { useState } from "react";
import {
  CalendarIcon,
  PhoneIcon,
  CheckCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";

// Pricing data
const pricingData = {
  consultations: [
    {
      name: "Initial consultation (1hr)",
      price: "£280",
      popular: true,
    },
    {
      name: "Virtual consultation",
      price: "£250",
      popular: false,
    },
    {
      name: "Initial consultation + 3D Ix USS",
      price: "£420",
      popular: false,
    },
    {
      name: "Follow-up consultation (30m)",
      price: "£180",
      popular: false,
    },
  ],
  ultrasound: [
    {
      name: "Investigation USS including 3D views",
      price: "£210",
      popular: true,
    },
    {
      name: "Follicle Tracking USS",
      price: "£180",
      popular: false,
    },
    {
      name: "Saline sonogram (3D acquascan) / HyCoSy",
      price: "£420",
      popular: false,
    },
    {
      name: "Saline sonogram and HyCoSy",
      price: "£440",
      popular: false,
    },
  ],
  bloodTests: [
    {
      name: "AMH",
      price: "£120",
      popular: false,
    },
    {
      name: "TSH",
      price: "£55",
      popular: false,
    },
    {
      name: "Rubella IgG",
      price: "£45",
      popular: false,
    },
    {
      name: "Virology",
      price: "£180",
      popular: false,
    },
  ],
};

const trustFeatures = [
  { icon: CheckCircleIcon, text: "Consultant-led" },
  { icon: ClockIcon, text: "Same-week availability" },
  { icon: ShieldCheckIcon, text: "Confidential" },
  { icon: DocumentTextIcon, text: "Digital reports" },
];

const whatsIncluded = {
  consultations: [
    "Comprehensive fertility assessment",
    "Discussion of your medical history",
    "Personalised treatment recommendations",
    "Time to ask questions and discuss concerns",
    "Follow-up communication as needed",
  ],
  ultrasound: [
    "High-resolution imaging",
    "3D visualization where applicable",
    "Detailed report and analysis",
    "Digital copy of images",
    "Consultation with results",
  ],
  bloodTests: [
    "Professional sample collection",
    "Laboratory analysis",
    "Digital results delivery",
    "Interpretation and explanation",
    "Follow-up recommendations if needed",
  ],
};

type TabType = "consultations" | "ultrasound" | "bloodTests";

export default function PricingPage() {
  const [activeTab, setActiveTab] = useState<TabType>("consultations");
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const tabs = [
    { id: "consultations" as TabType, label: "Consultations" },
    { id: "ultrasound" as TabType, label: "Ultrasound" },
    { id: "bloodTests" as TabType, label: "Blood Tests" },
  ];

  const currentPricing = pricingData[activeTab];
  const currentIncludes = whatsIncluded[activeTab];

  return (
    <main className="min-h-screen pb-20 md:pb-0" data-oid="pricing-page">
      {/* Hero Section */}
      <section
        className="bg-gradient-to-r from-brand-blue/10 to-brand-pink/10 py-16"
        data-oid="pricing-hero"
      >
        <div className="container mx-auto px-4" data-oid="pricing-hero-container">
          <div className="max-w-3xl mx-auto text-center" data-oid="pricing-hero-content">
            <h1
              className="mb-6 text-4xl md:text-5xl font-bold text-brand-blue"
              data-oid="pricing-title"
            >
              Pricing
            </h1>
            <p className="text-xl text-gray-600 mb-8" data-oid="pricing-subtitle">
              Transparent fees for consultations, ultrasound scans, and common
              blood tests.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
              data-oid="pricing-ctas"
            >
              <Link
                href="/contact"
                className="inline-flex items-center justify-center bg-brand-pink text-white px-8 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors"
                data-oid="book-appointment-btn"
              >
                <CalendarIcon className="w-5 h-5 mr-2" />
                Book an appointment
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center border-2 border-brand-blue text-brand-blue px-8 py-3 rounded-full font-semibold hover:bg-brand-blue/10 transition-colors"
                data-oid="contact-btn"
              >
                <PhoneIcon className="w-5 h-5 mr-2" />
                Contact us
              </Link>
            </div>
            {/* Trust Strip */}
            <div
              className="flex flex-wrap justify-center gap-6 md:gap-8 pt-8 border-t border-gray-200"
              data-oid="trust-strip"
            >
              {trustFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-gray-700"
                  data-oid={`trust-feature-${index}`}
                >
                  <feature.icon className="w-5 h-5 text-brand-pink" />
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <div className="bg-gray-50 py-4" data-oid="pricing-note">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-600" data-oid="pricing-note-text">
            Prices in GBP (£). Subject to change.
          </p>
        </div>
      </div>

      {/* Pricing Sections */}
      <section className="py-16" data-oid="pricing-sections">
        <div className="container mx-auto px-4" data-oid="pricing-sections-container">
          {/* Tabs/Segmented Control */}
          <div
            className="flex flex-wrap justify-center gap-2 mb-12 bg-gray-100 p-2 rounded-lg max-w-2xl mx-auto"
            data-oid="pricing-tabs"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[120px] px-6 py-3 rounded-md font-semibold transition-all ${
                  activeTab === tab.id
                    ? "bg-brand-pink text-white shadow-md"
                    : "bg-transparent text-gray-700 hover:bg-gray-200"
                }`}
                data-oid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Pricing Cards */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
              data-oid="pricing-cards"
            >
              <div
                className="bg-white rounded-lg shadow-sm border p-8 mb-6"
                data-oid="pricing-card-container"
              >
                <h2
                  className="text-2xl font-bold text-brand-blue mb-6"
                  data-oid="pricing-section-title"
                >
                  {tabs.find((t) => t.id === activeTab)?.label}
                </h2>
                {activeTab === "bloodTests" && (
                  <p className="text-sm text-gray-600 mb-6 italic" data-oid="blood-tests-note">
                    Many others available — POA
                  </p>
                )}
                <div className="space-y-4" data-oid="pricing-items">
                  {currentPricing.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0"
                      data-oid={`pricing-item-${index}`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-gray-900 font-medium" data-oid={`item-name-${index}`}>
                          {item.name}
                        </span>
                        {item.popular && (
                          <span
                            className="px-3 py-1 text-xs font-semibold bg-brand-pink text-white rounded-full"
                            data-oid={`popular-badge-${index}`}
                          >
                            Most popular
                          </span>
                        )}
                      </div>
                      <span
                        className="text-2xl font-bold text-brand-blue ml-4"
                        data-oid={`item-price-${index}`}
                      >
                        {item.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What's Included Accordion */}
              <div
                className="bg-white rounded-lg shadow-sm border p-8"
                data-oid="whats-included-accordion"
              >
                <button
                  onClick={() =>
                    setOpenAccordion(openAccordion === activeTab ? null : activeTab)
                  }
                  className="w-full flex items-center justify-between text-left"
                  data-oid="whats-included-toggle"
                >
                  <h3
                    className="text-xl font-semibold text-brand-blue"
                    data-oid="whats-included-title"
                  >
                    What's included
                  </h3>
                  <FaChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openAccordion === activeTab ? "transform rotate-180" : ""
                    }`}
                    data-oid="whats-included-chevron"
                  />
                </button>
                <AnimatePresence>
                  {openAccordion === activeTab && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                      data-oid="whats-included-content"
                    >
                      <ul className="mt-6 space-y-3" data-oid="whats-included-list">
                        {currentIncludes.map((item, index) => (
                          <li
                            key={index}
                            className="flex items-start gap-3 text-gray-600"
                            data-oid={`whats-included-item-${index}`}
                          >
                            <CheckCircleIcon className="w-5 h-5 text-brand-pink flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Sticky Mobile Bottom Bar */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 md:hidden"
        data-oid="mobile-bottom-bar"
      >
        <div className="container mx-auto px-4 py-4" data-oid="mobile-bottom-bar-container">
          <Link
            href="/contact"
            className="w-full inline-flex items-center justify-center bg-brand-pink text-white px-6 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors"
            data-oid="mobile-book-btn"
          >
            <CalendarIcon className="w-5 h-5 mr-2" />
            Book an appointment
          </Link>
        </div>
      </div>
    </main>
  );
}

